import {
  TransportSedel,
  TransportSedelStatus,
} from "types/transportSedel.type";
import instance, { ApiUrl } from "./axiosInstance";
import { Waste } from "types/waste.type";
import { TTransportSedelSchema } from "./uppdrag.api";
import { queryOptions, useMutation } from "@tanstack/react-query";
import { WasteStation } from "types/wasteStation.type";
import { Vehicle } from "types/vehicle.type";
import { useNotification } from "hooks/useNotification";
import { Facility } from "types/facility.type";
import { NvRapporteringStatus } from "enums/NvRapporteringStatus.enum";

export enum TransportSedelQueryKey {
  GetList = "get-transport-sedel-list",
  GetOne = "get-transport-sedel",
}

interface GetTransportSedelListParams {
  companyId: number;
  statuses?: TransportSedelStatus[];
  userId?: number;
}

export const getTransportSedelList = (params: GetTransportSedelListParams) =>
  queryOptions({
    queryKey: [TransportSedelQueryKey.GetList, params],
    queryFn: async (): Promise<LoadedTransportSedelResponse> =>
      (await instance.get(ApiUrl.TransportSedel, { params })).data,
  });

export const getTransportSedel = (id: number) =>
  queryOptions({
    queryKey: [TransportSedelQueryKey.GetOne, id],
    queryFn: async (): Promise<TransportSedel> => {
      const response = await instance.get(`${ApiUrl.TransportSedel}/${id}`);
      return response.data;
    },
    enabled: id > 0,
  });

export type LoadedTransportSedelResponse = {
  activeGroups: TransportSedelGroup[];
  inactiveGroups: TransportSedelGroup[];
  activeVehicles: Vehicle[];
  activeWasteCount: number;
};

export type TransportSedelGroup = {
  wasteStation: WasteStation;
  vehicles: {
    vehicle: Vehicle;
    transportSedelList: TransportSedel[];
  }[];
};

export const patchTransportSedel = () => ({
  mutationFn: async (data: {
    payload: Partial<TTransportSedelSchema>;
    id: number;
  }): Promise<TransportSedel> =>
    (await instance.patch(`${ApiUrl.TransportSedel}/${data.id}`, data.payload))
      .data,
});

export const deleteTransportSedel = () => ({
  mutationFn: async (id: number): Promise<void> =>
    (await instance.delete(`${ApiUrl.TransportSedel}/${id}`)).data,
});

export const useSetFinalWeight = (settings = {}) => {
  const notification = useNotification();

  return useMutation({
    mutationFn: async (payload: {
      wasteList: { id: number; weightFinal: string }[];
    }): Promise<WasteStationSignatureResult[]> => {
      const response = await instance.put(
        `${ApiUrl.TransportSedel}/final-weight`,
        payload,
      );
      return response.data;
    },
    onError: () => {
      notification.error("Något gick fel, kontakta support");
    },
    ...settings,
  });
};

export type WasteDto = {
  wasteCodeUuid?: string;
  weightPreliminary?: string;
  weightFinal?: string;
  quantity?: string;
  // isDangerousGoods?: boolean;
  // unNumber?: string;
  wasteType?: "Weight" | "Quantity";
  tomningSeparatorUuid?: string | null;
  tomningType?: "Heltomning" | "Toppsugning" | "NotApplicable" | "NotAssigned";
  comment?: string;
  transportRapporteringStatus?: NvRapporteringStatus;
  ombudsRapporteringStatus?: NvRapporteringStatus;
};

export const createTransportSedelWaste = (transportSedelId: number) => ({
  mutationFn: async (dto: WasteDto): Promise<Waste> =>
    (
      await instance.post(
        `${ApiUrl.TransportSedel}/${transportSedelId}/waste`,
        dto,
      )
    ).data,
});

export const patchTransportSedelWaste = () => ({
  mutationFn: async (data: {
    payload: WasteDto;
    transportSedelId: number;
    wasteId: number;
  }): Promise<TransportSedel> =>
    (
      await instance.post(
        `${ApiUrl.TransportSedel}/${data.transportSedelId}/waste/${data.wasteId}`,
        data.payload,
      )
    ).data,
});

export type DeleteTransportSedelWasteInput = {
  transportSedelId: number;
  wasteId: number;
};
export const deleteTransportSedelWaste = () => ({
  mutationFn: async ({
    transportSedelId,
    wasteId,
  }: DeleteTransportSedelWasteInput): Promise<void> =>
    (
      await instance.delete(
        `${ApiUrl.TransportSedel}/${transportSedelId}/waste/${wasteId}`,
      )
    ).data,
});

export type WasteStationSignatureResult = {
  isReadyToBeCompleted: boolean;
  facility: Facility;
  uppdragId: number;
  uppdragUuid: string;
  estimatedWorkingHours?: string;
  estimatedDrivingHours?: string;
};

export const useUpdateTransportSedelWasteStationSignature = (settings = {}) => {
  const notification = useNotification();

  return useMutation({
    mutationFn: async (
      payload: WasteStationSignatureListDTO,
    ): Promise<WasteStationSignatureResult[]> =>
      (
        await instance.put(
          `${ApiUrl.TransportSedel}/waste-station-signature`,
          payload,
        )
      ).data,
    onError: () => {
      notification.error("Något gick fel, kontakta support");
    },
    ...settings,
  });
};

export type WasteStationSignatureDTO = {
  transportSedelId: number;
  wasteStationSignature: string;
};

type WasteStationSignatureListDTO = {
  wasteStationSignatureList: WasteStationSignatureDTO[];
  sendToWasteStation: boolean;
};
